import { Option } from "@jutro/types";
import { BinIcon, Button, ErrorMessage, Input } from "@jutro/ui";
import { Dayjs } from "dayjs";
import { Controller } from "react-hook-form";
import ReactModal from "react-modal";
import { useStaffDetailsModalForm } from "views/StaffSchedule/StaffScheduleCreator/hooks/useStaffDetailsModalForm";
import { StaffOption } from "views/StaffSchedule/StaffScheduleCreator/tools";

type Props = {
  isModalOpen: boolean;
  staff: Option<StaffOption> | null;
  selectedDate: Dayjs;
  handleCloseModal: () => void;
};

export const StaffDetailsActionModal = ({
  isModalOpen,
  staff,
  selectedDate,
  handleCloseModal,
}: Props) => {
  const {
    control,
    errors,
    createWorkingHoursLoading,
    deleteWorkingHoursLoading,
    handleSubmit,
    onDeleteWorkingHours,
    onSubmit,
  } = useStaffDetailsModalForm({
    selectedDate,
    staffId: staff?.value.id ?? "",
    handleCloseModal,
  });

  const { hours: hoursFieldError, minutes: minutesFieldError } = errors;
  return (
    <ReactModal
      isOpen={isModalOpen}
      shouldCloseOnEsc
      onRequestClose={() => {}}
      className="z-50 flex h-full w-full flex-col items-center justify-center gap-2 text-jutro-new-warm-gray-800"
      ariaHideApp={false}
      shouldCloseOnOverlayClick
      style={{
        overlay: { zIndex: 50, backgroundColor: "rgba(24, 24, 26, 0.5)" },
      }}
    >
      <form className="flex w-[520px] flex-col gap-4 bg-white px-8 py-6">
        <span className="font-heading-3">{staff?.label}</span>
        <span className="font-paragraph-1">
          Wprowadź miesięczny wymiar czasu pracy, żeby zobaczyć bilans
          planowanych i oczekiwanych godzin pracy.
        </span>
        <div className="flex w-60 flex-col gap-1">
          <span className="font-label">Miesięczny wymiar czasu pracy</span>
          <div className="flex w-52 gap-2">
            <Controller
              control={control}
              name="hours"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Input
                  value={value}
                  type="number"
                  placeholder="HHH"
                  sideElement="h"
                  sideElementPosition="end"
                  maxLength={3}
                  error={Boolean(error || hoursFieldError || minutesFieldError)}
                  onChange={onChange}
                />
              )}
            />

            <Controller
              control={control}
              name="minutes"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Input
                  value={value}
                  type="number"
                  placeholder="MM"
                  sideElement="min"
                  sideElementPosition="end"
                  maxLength={2}
                  max={59}
                  error={Boolean(error || hoursFieldError || minutesFieldError)}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);

                    if (value > 59) {
                      return;
                    }
                    onChange(value.toString());
                  }}
                />
              )}
            />
          </div>
          {Boolean(hoursFieldError || minutesFieldError) && (
            <ErrorMessage
              error={hoursFieldError?.message || minutesFieldError?.message}
            />
          )}
        </div>

        <div className="flex justify-between">
          <div>
            <Button
              text="Usuń"
              variant="negativeGhost"
              type="button"
              icon={<BinIcon />}
              full={false}
              loading={deleteWorkingHoursLoading || createWorkingHoursLoading}
              onClick={onDeleteWorkingHours}
            />
          </div>

          <div className="flex gap-4">
            <Button
              text="Anuluj"
              variant="ghost"
              type="button"
              full={false}
              onClick={handleCloseModal}
            />
            <Button
              text="Zapisz"
              type="button"
              full={false}
              loading={deleteWorkingHoursLoading || createWorkingHoursLoading}
              onClick={handleSubmit((data) => onSubmit(data))}
            />
          </div>
        </div>
      </form>
    </ReactModal>
  );
};
